import React, { useEffect, useState } from "react";
import { Modal, Button } from "antd";
import {
  RotateLeftOutlined,
  RotateRightOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
  LeftOutlined,
  RightOutlined,
} from "@ant-design/icons";

interface PreviewFile {
  url: string;
  name: string;
  showUpload: boolean;
  documentType: string;
}

interface FilePreviewModalProps {
  isVisible: boolean;
  onClose: () => void;
  files: PreviewFile[]; // Now an array
  handleUpload: any;
  showUploadButton: boolean;
  key?: any;
  isLoading?: boolean;
}

const MultipleFilesPreviewModal: React.FC<FilePreviewModalProps> = ({
  isVisible,
  onClose,
  files = [],
  handleUpload,
  showUploadButton,
  isLoading,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [rotationAngle, setRotationAngle] = useState(0);

  const currentFile = files[currentIndex] || { url: "", name: "", showUpload: false, documentType: "" };
  const isPdf = currentFile.name?.toLowerCase().endsWith(".pdf");

  useEffect(() => {
    setZoomLevel(1);
    setRotationAngle(0);
  }, [currentIndex, isVisible]);

  useEffect(() => {
    if (!isVisible) setCurrentIndex(0);
  }, [isVisible, files.length]);

  const handleZoomIn = () => setZoomLevel((prevZoom) => Math.min(prevZoom + 0.1, 3));
  const handleZoomOut = () => setZoomLevel((prevZoom) => Math.max(prevZoom - 0.1, 0.5));
  const handleRotateLeft = () => setRotationAngle((prev) => prev - 90);
  const handleRotateRight = () => setRotationAngle((prev) => prev + 90);

  const handlePrev = () => setCurrentIndex((prev) => Math.max(prev - 1, 0));
  const handleNext = () => setCurrentIndex((prev) => Math.min(prev + 1, files.length - 1));

  return (
    <div className="preview-modal">
    <Modal
      title={
        files.length > 1
          ? `Preview (${currentIndex + 1}/${files.length}): ${currentFile.name}`
          : `Preview: ${currentFile.name}`
      }
      open={isVisible}
      onCancel={onClose}
      footer={[
        showUploadButton && (
          <Button
            key="upload"
            type="primary"
            onClick={handleUpload}
            loading={isLoading}
            style={{marginTop: "0.6rem"}}
          >
            Upload
          </Button>
        ),
        <Button key="close" onClick={onClose} style={{marginTop: "0.8rem"}}>
          Close
        </Button>,
      ].filter(Boolean)}
      width={600}
      bodyStyle={{ height: "120vh" }}
      wrapClassName="preview-modal"
    >
      <div style={{ display: "flex", alignItems: "center", height: "100%" }}>
        {files.length > 1 && (
          <Button
            icon={<LeftOutlined />}
            onClick={handlePrev}
            disabled={currentIndex === 0}
            style={{ marginRight: 8 }}
          />
        )}
        <div style={{ flex: 1, position: "relative", height: "100%" }}>
          {isPdf ? (
            <object
              data={currentFile.url}
              type="application/pdf"
              width="100%"
              height="100%"
            >
              <embed
                src={currentFile.url}
                type="application/pdf"
                width="100%"
                height="100%"
              />
              <p>
                This browser does not support PDFs. Please download the PDF to view
                it:
                <a href={currentFile.url} download={currentFile.name}>
                  Download PDF
                </a>
                .
              </p>
            </object>
          ) : (
            <>
              <div
                style={{
                  overflow: "auto",
                  width: "100%",
                  height: "120vh",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "flex-start",
                  marginTop: 20,
                }}
              >
                <div
                  style={{
                    minWidth: `${100 * zoomLevel}%`,
                    minHeight: `${100 * zoomLevel}%`,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <img
                    src={currentFile.url}
                    alt={currentFile.name}
                    style={{
                      transform: `scale(${zoomLevel}) rotate(${rotationAngle}deg)`,
                      transition: "transform 0.3s ease",
                      maxWidth: "none",
                      maxHeight: "none",
                      width: "100%",
                      height: "auto",
                    }}
                  />
                </div>
              </div>
              <div className="zoom-controls" style={{ marginTop: "0.3rem" }}>
                <Button
                  onClick={handleZoomOut}
                  disabled={zoomLevel <= 0.5}
                  style={{ marginRight: "0.5rem" }}
                >
                  <ZoomOutOutlined />
                </Button>
                <Button onClick={handleZoomIn} disabled={zoomLevel >= 3}>
                  <ZoomInOutlined />
                </Button>
                <Button onClick={handleRotateLeft} style={{ marginLeft: 8 }}>
                  <RotateLeftOutlined />
                </Button>
                <Button onClick={handleRotateRight} style={{ marginLeft: 8 }}>
                  <RotateRightOutlined />
                </Button>
              </div>
            </>
          )}
          </div>
        {files.length > 1 && (
          <Button
            icon={<RightOutlined />}
            onClick={handleNext}
            disabled={currentIndex === files.length - 1}
            style={{ marginLeft: 8 }}
          />
        )}
      </div>
    </Modal>
    </div>
  );
};

export default MultipleFilesPreviewModal;